
import { useState } from 'react';

export default function QuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    drivewayLength: '',
    drivewayWidth: '',
    bollardType: '',
    quantity: '',
    accessRequirements: '',
    groundType: '',
    additionalInfo: '',
    photos: [] as File[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData(prev => ({
        ...prev,
        photos: Array.from(e.target.files || [])
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new FormData();
      
      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'photos') {
          // Handle file uploads - show as "Uncollectable" as per requirements
          submitData.append(key, 'Uncollectable');
        } else {
          submitData.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/submit/bollard-quote-request', {
        method: 'POST',
        body: submitData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          drivewayLength: '',
          drivewayWidth: '',
          bollardType: '',
          quantity: '',
          accessRequirements: '',
          groundType: '',
          additionalInfo: '',
          photos: []
        });
        // Reset file input
        const fileInput = document.getElementById('photos') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <form id="bollard-quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
        {/* Contact Information */}
        <div className="border-b border-gray-200 pb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter your email address"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter your phone number"
              />
            </div>
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                Property Address *
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Enter the installation address"
              />
            </div>
          </div>
        </div>

        {/* Driveway Specifications */}
        <div className="border-b border-gray-200 pb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Driveway Specifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="drivewayLength" className="block text-sm font-medium text-gray-700 mb-2">
                Driveway Length (meters) *
              </label>
              <input
                type="number"
                id="drivewayLength"
                name="drivewayLength"
                value={formData.drivewayLength}
                onChange={handleInputChange}
                required
                min="1"
                step="0.1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="e.g., 5.5"
              />
            </div>
            <div>
              <label htmlFor="drivewayWidth" className="block text-sm font-medium text-gray-700 mb-2">
                Driveway Width (meters) *
              </label>
              <input
                type="number"
                id="drivewayWidth"
                name="drivewayWidth"
                value={formData.drivewayWidth}
                onChange={handleInputChange}
                required
                min="1"
                step="0.1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="e.g., 3.0"
              />
            </div>
            <div>
              <label htmlFor="groundType" className="block text-sm font-medium text-gray-700 mb-2">
                Ground Surface Type *
              </label>
              <div className="relative">
                <select
                  id="groundType"
                  name="groundType"
                  value={formData.groundType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none cursor-pointer"
                >
                  <option value="">Select ground type</option>
                  <option value="concrete">Concrete</option>
                  <option value="asphalt">Asphalt</option>
                  <option value="block-paving">Block Paving</option>
                  <option value="gravel">Gravel</option>
                  <option value="grass">Grass</option>
                  <option value="other">Other</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="accessRequirements" className="block text-sm font-medium text-gray-700 mb-2">
                Access Requirements
              </label>
              <input
                type="text"
                id="accessRequirements"
                name="accessRequirements"
                value={formData.accessRequirements}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="e.g., narrow access, parking restrictions"
              />
            </div>
          </div>
        </div>

        {/* Bollard Requirements */}
        <div className="border-b border-gray-200 pb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Bollard Requirements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="bollardType" className="block text-sm font-medium text-gray-700 mb-2">
                Bollard Type *
              </label>
              <div className="relative">
                <select
                  id="bollardType"
                  name="bollardType"
                  value={formData.bollardType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none cursor-pointer"
                >
                  <option value="">Select bollard type</option>
                  <option value="telescopic">Telescopic Bollard</option>
                  <option value="fold-down">Fold Down Bollard</option>
                  <option value="electric">Electric Bollard</option>
                  <option value="fixed">Fixed Bollard</option>
                  <option value="removable">Removable Bollard</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                Number of Bollards *
              </label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                required
                min="1"
                max="20"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="e.g., 2"
              />
            </div>
          </div>
        </div>

        {/* Photo Upload */}
        <div className="border-b border-gray-200 pb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Photos</h2>
          <div>
            <label htmlFor="photos" className="block text-sm font-medium text-gray-700 mb-2">
              Upload Driveway Photos
            </label>
            <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 transition-colors">
              <div className="space-y-1 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center">
                  <i className="ri-upload-cloud-2-line text-3xl text-gray-400"></i>
                </div>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="photos"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload photos</span>
                    <input
                      id="photos"
                      name="photos"
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                      className="sr-only"
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
              </div>
            </div>
            {formData.photos.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Selected files:</p>
                <ul className="text-sm text-gray-500">
                  {formData.photos.map((file, index) => (
                    <li key={index} className="flex items-center">
                      <i className="ri-file-image-line mr-2"></i>
                      {file.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Additional Information</h2>
          <div>
            <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Requirements or Comments
            </label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleInputChange}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
              placeholder="Please provide any additional details about your requirements, preferred installation dates, or special considerations..."
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.additionalInfo.length}/500 characters
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-6">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Submitting Quote Request...
              </span>
            ) : (
              'Submit Quote Request'
            )}
          </button>
        </div>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <i className="ri-check-circle-line text-green-600 mr-2"></i>
              <p className="text-green-800 font-medium">
                Quote request submitted successfully! We'll contact you within 24 hours.
              </p>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-red-600 mr-2"></i>
              <p className="text-red-800 font-medium">
                There was an error submitting your request. Please try again or contact us directly.
              </p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
